import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';

export const ReviewTable = ({
  reviews,
  selectedReviews,
  setSelectedReviews,
  reviewSortBy,
  setReviewSortBy,
  reviewSortOrder,
  setReviewSortOrder,
  reviewFilterBy,
  setReviewFilterBy,
  reviewSearchTerm,
  setReviewSearchTerm,
  onDeleteReview,
  onBulkDelete,
  onExport,
  onPrint,
  onShowHistory,
  onRefresh
}) => {
  const ratingLabels = ['', 'MAJOR ISSUES', 'NEEDS ATTENTION', "IT'S OK", 'EXCEPTIONAL'];
  const ratingColors = ['', 'bg-red-100 text-red-700', 'bg-orange-100 text-orange-700', 'bg-yellow-100 text-yellow-700', 'bg-green-100 text-green-700'];

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // Normalize restroom types from legacy format to current format
  const normalizeRestroomTypes = (types) => {
    if (!types || !Array.isArray(types)) return [];
    return types.map(type => {
      // Map legacy lowercase values to current format
      const typeMap = {
        'family': 'Family',
        'handicap': 'Wheelchair Accessible',
        'gender_neutral': 'Gender Neutral',
        'accessible': 'Wheelchair Accessible',
        'men': 'Men',
        'women': 'Women',
        'single': 'Gender Neutral'
      };
      return typeMap[type.toLowerCase()] || type;
    });
  };

  const handleEditClick = (review) => {
    setEditingReview(review);
    setEditFormData({
      restaurant_name: review.restaurant_name || '',
      address: review.address || '',
      phone: review.phone || '',
      city: review.city || '',
      state: review.state || '',
      overall_rating: review.overall_rating || 0,
      general_condition: review.general_condition || '5',
      supplies: review.supplies || '5',
      maintenance: review.maintenance || '5',
      restroom_types: normalizeRestroomTypes(review.restroom_types),
      comments: review.comments || '',
      reviewer_name: review.reviewer_name || ''
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingReview) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      // Ensure rating fields are integers
      const dataToSend = {
        ...editFormData,
        general_condition: parseInt(editFormData.general_condition, 10) || 5,
        supplies: parseInt(editFormData.supplies, 10) || 5,
        maintenance: parseInt(editFormData.maintenance, 10) || 5
      };
      await axios.put(`${API}/admin/reviews/${editingReview.id}`, dataToSend, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Review updated successfully!');
      setEditModalOpen(false);
      setEditingReview(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      // Handle validation errors (array of detail objects)
      const detail = error.response?.data?.detail;
      let errorMsg = 'Failed to update review';
      if (Array.isArray(detail)) {
        errorMsg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
      } else if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleRestroomTypeToggle = (type) => {
    const types = editFormData.restroom_types || [];
    if (types.includes(type)) {
      setEditFormData({...editFormData, restroom_types: types.filter(t => t !== type)});
    } else {
      setEditFormData({...editFormData, restroom_types: [...types, type]});
    }
  };

  // Sorting helper
  const sortData = (data, sortBy, sortOrder) => {
    return [...data].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === 'created_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  };

  // Get filtered and sorted reviews
  const getFilteredReviews = () => {
    let filtered = [...reviews];
    
    // Apply filter
    if (reviewFilterBy !== 'all') {
      const rating = parseInt(reviewFilterBy);
      filtered = filtered.filter(r => r.overall_rating === rating);
    }
    
    // Apply search
    if (reviewSearchTerm) {
      const term = reviewSearchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.restaurant_name.toLowerCase().includes(term) || 
        r.city.toLowerCase().includes(term) ||
        r.reviewer_name.toLowerCase().includes(term)
      );
    }
    
    // Apply sort
    return sortData(filtered, reviewSortBy, reviewSortOrder);
  };

  const handleSelectAllReviews = (checked) => {
    if (checked) {
      setSelectedReviews(getFilteredReviews().map(r => r.id));
    } else {
      setSelectedReviews([]);
    }
  };

  const filteredReviews = getFilteredReviews();

  return (
    <div className="space-y-4">
      <h2 className="text-3xl text-red-600 mb-4" 
          style={{ 
            fontFamily: 'Bebas Neue, sans-serif',
            letterSpacing: '4px',
            textShadow: 'none',
            fontWeight: '400'
          }}>
        MANAGE REVIEWS
      </h2>
      
      {/* Toolbar */}
      <div className="bg-white p-4 rounded border-2 border-black flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search */}
          <Input
            placeholder="Search restaurant, city, reviewer..."
            value={reviewSearchTerm}
            onChange={(e) => setReviewSearchTerm(e.target.value)}
            className="w-64 border-2 border-black"
          />
          
          {/* Sort By */}
          <select
            value={reviewSortBy}
            onChange={(e) => setReviewSortBy(e.target.value)}
            className="px-3 py-2 border-2 border-black rounded bg-white font-semibold"
          >
            <option value="created_at">Sort: Date</option>
            <option value="restaurant_name">Sort: Restaurant</option>
            <option value="city">Sort: City</option>
            <option value="overall_rating">Sort: Rating</option>
            <option value="reviewer_name">Sort: Reviewer</option>
          </select>
          
          {/* Sort Order */}
          <Button
            onClick={() => setReviewSortOrder(reviewSortOrder === 'asc' ? 'desc' : 'asc')}
            variant="outline"
            className="border-2 border-black"
          >
            {reviewSortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
          </Button>
          
          {/* Filter By Rating */}
          <select
            value={reviewFilterBy}
            onChange={(e) => setReviewFilterBy(e.target.value)}
            className="px-3 py-2 border-2 border-black rounded bg-white font-semibold"
          >
            <option value="all">Filter: All Ratings</option>
            <option value="4">Filter: Perfect</option>
            <option value="3">Filter: Acceptable</option>
            <option value="2">Filter: Needs Work</option>
            <option value="1">Filter: Disgusting</option>
          </select>
        </div>
        
        <div className="flex gap-3">
          {selectedReviews.length > 0 && (
            <Button
              onClick={onBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white border-2 border-black font-bold"
            >
              🗑️ Delete Selected ({selectedReviews.length})
            </Button>
          )}
          <Button
            onClick={onShowHistory}
            className="bg-gray-600 hover:bg-gray-700 text-white border-2 border-black font-bold"
          >
            📋 History
          </Button>
          <Button
            onClick={onExport}
            className="bg-green-600 hover:bg-green-700 text-white border-2 border-black font-bold"
            data-testid="export-reviews-csv"
          >
            📊 Export CSV
          </Button>
          <Button
            onClick={onPrint}
            className="bg-blue-600 hover:bg-blue-700 text-white border-2 border-black font-bold"
            data-testid="print-reviews"
          >
            🖨️ Print
          </Button>
        </div>
      </div>
      
      {/* Spreadsheet Table */}
      <div className="bg-white rounded border-2 border-black overflow-x-auto">
        <table className="w-full min-w-[1200px]">
          <thead className="bg-gray-100 border-b-2 border-black">
            <tr>
              <th className="p-3 text-left w-12">
                <Checkbox
                  checked={selectedReviews.length === filteredReviews.length && filteredReviews.length > 0}
                  onCheckedChange={handleSelectAllReviews}
                />
              </th>
              <th className="p-3 text-left font-bold text-gray-800">Restaurant</th>
              <th className="p-3 text-left font-bold text-gray-800">City</th>
              <th className="p-3 text-left font-bold text-gray-800">Phone</th>
              <th className="p-3 text-left font-bold text-gray-800">Rating</th>
              <th className="p-3 text-left font-bold text-gray-800">Clean</th>
              <th className="p-3 text-left font-bold text-gray-800">Supplies</th>
              <th className="p-3 text-left font-bold text-gray-800">Maint</th>
              <th className="p-3 text-left font-bold text-gray-800">Types</th>
              <th className="p-3 text-left font-bold text-gray-800">Reviewer</th>
              <th className="p-3 text-left font-bold text-gray-800">Date</th>
              <th className="p-3 text-left font-bold text-gray-800">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReviews.map((r, idx) => (
              <tr key={r.id} className={`border-b border-gray-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-yellow-50`}>
                <td className="p-3">
                  <Checkbox
                    checked={selectedReviews.includes(r.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedReviews([...selectedReviews, r.id]);
                      } else {
                        setSelectedReviews(selectedReviews.filter(id => id !== r.id));
                      }
                    }}
                  />
                </td>
                <td className="p-3 font-semibold text-red-600 max-w-[200px] truncate" title={r.restaurant_name}>{r.restaurant_name}</td>
                <td className="p-3 text-gray-600">{r.city}</td>
                <td className="p-3 text-gray-600 text-sm">{r.phone || '-'}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 text-xs font-bold rounded ${ratingColors[r.overall_rating]}`}>
                    {ratingLabels[r.overall_rating]}
                  </span>
                </td>
                <td className="p-3 text-center">{r.general_condition}/10</td>
                <td className="p-3 text-center">{r.supplies}/10</td>
                <td className="p-3 text-center">{r.maintenance}/10</td>
                <td className="p-3 text-xs text-gray-600">{(r.restroom_types || []).join(', ') || '-'}</td>
                <td className="p-3 text-gray-700 text-sm">
                  {r.reviewer_name?.toLowerCase().includes('anonymous') ? 'Verified User Review' : r.reviewer_name}
                </td>
                <td className="p-3 text-gray-600 text-sm">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="p-3">
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      onClick={() => handleEditClick(r)}
                      size="sm"
                      variant="outline"
                      className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white text-xs"
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      onClick={() => onDeleteReview(r.id, r.restaurant_name)}
                      size="sm"
                      variant="outline"
                      className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white text-xs"
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredReviews.length === 0 && (
          <div className="p-8 text-center text-gray-500">No reviews found matching your criteria</div>
        )}
      </div>
      <div className="text-sm text-gray-600">
        Showing {filteredReviews.length} of {reviews.length} reviews
      </div>

      {/* Edit Review Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-4 border-black">
          <DialogHeader>
            <DialogTitle className="text-red-600 font-bold text-2xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              ✏️ Edit Review
            </DialogTitle>
          </DialogHeader>
          
          {editingReview && (
            <div className="space-y-4 mt-4">
              {/* Restaurant Name */}
              <div>
                <Label className="font-bold text-gray-700">Restaurant Name</Label>
                <Input
                  value={editFormData.restaurant_name}
                  onChange={(e) => setEditFormData({...editFormData, restaurant_name: e.target.value})}
                  className="border-2 border-gray-300 mt-1"
                />
              </div>
              
              {/* Address */}
              <div>
                <Label className="font-bold text-gray-700">Address</Label>
                <Input
                  value={editFormData.address}
                  onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                  className="border-2 border-gray-300 mt-1"
                />
              </div>
              
              {/* City & State */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-bold text-gray-700">City</Label>
                  <Input
                    value={editFormData.city}
                    onChange={(e) => setEditFormData({...editFormData, city: e.target.value})}
                    className="border-2 border-gray-300 mt-1"
                  />
                </div>
                <div>
                  <Label className="font-bold text-gray-700">State</Label>
                  <Input
                    value={editFormData.state}
                    onChange={(e) => setEditFormData({...editFormData, state: e.target.value})}
                    className="border-2 border-gray-300 mt-1"
                  />
                </div>
              </div>
              
              {/* Phone */}
              <div>
                <Label className="font-bold text-gray-700">Phone</Label>
                <Input
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                  className="border-2 border-gray-300 mt-1"
                />
              </div>
              
              {/* Overall Rating */}
              <div>
                <Label className="font-bold text-gray-700">Overall Rating</Label>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setEditFormData({...editFormData, overall_rating: rating})}
                      className={`px-3 py-2 rounded-lg border-2 text-xs font-bold transition-all ${
                        editFormData.overall_rating === rating 
                          ? ratingColors[rating] + ' border-black scale-105' 
                          : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {ratingLabels[rating]}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Detailed Ratings */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="font-bold text-gray-700">Cleanliness (1-10)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={editFormData.general_condition}
                    onChange={(e) => setEditFormData({...editFormData, general_condition: e.target.value})}
                    className="border-2 border-gray-300 mt-1"
                  />
                </div>
                <div>
                  <Label className="font-bold text-gray-700">Supplies (1-10)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={editFormData.supplies}
                    onChange={(e) => setEditFormData({...editFormData, supplies: e.target.value})}
                    className="border-2 border-gray-300 mt-1"
                  />
                </div>
                <div>
                  <Label className="font-bold text-gray-700">Maintenance (1-10)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={editFormData.maintenance}
                    onChange={(e) => setEditFormData({...editFormData, maintenance: e.target.value})}
                    className="border-2 border-gray-300 mt-1"
                  />
                </div>
              </div>
              
              {/* Restroom Types */}
              <div>
                <Label className="font-bold text-gray-700">Restroom Types</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['Men', 'Women', 'Gender Neutral', 'Family', 'Wheelchair Accessible'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleRestroomTypeToggle(type)}
                      className={`px-3 py-1 rounded-full border-2 text-sm font-bold transition-all ${
                        (editFormData.restroom_types || []).includes(type)
                          ? 'bg-blue-500 text-white border-blue-600'
                          : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {type === 'Men' && '🚹 '}
                      {type === 'Women' && '🚺 '}
                      {type === 'Gender Neutral' && '🚻 '}
                      {type === 'Family' && '👨‍👩‍👧 '}
                      {type === 'Wheelchair Accessible' && '♿ '}
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Reviewer Name */}
              <div>
                <Label className="font-bold text-gray-700">Reviewer Name</Label>
                <Input
                  value={editFormData.reviewer_name}
                  onChange={(e) => setEditFormData({...editFormData, reviewer_name: e.target.value})}
                  className="border-2 border-gray-300 mt-1"
                />
              </div>
              
              {/* Comments */}
              <div>
                <Label className="font-bold text-gray-700">Comments</Label>
                <Textarea
                  value={editFormData.comments}
                  onChange={(e) => setEditFormData({...editFormData, comments: e.target.value})}
                  className="border-2 border-gray-300 mt-1"
                  rows={3}
                />
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setEditModalOpen(false)}
                  variant="outline"
                  className="flex-1 border-2 border-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold border-2 border-green-800"
                >
                  {saving ? 'Saving...' : '💾 Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
